import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';
import { List, X } from '@phosphor-icons/react';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import SalesPage from './pages/SalesPage';
import PaymentsValidationPage from './pages/PaymentsValidationPage';
import UsersPage from './pages/UsersPage';
import LocationsPage from './pages/LocationsPage';
import ProductsPage from './pages/ProductsPage';
import AttendancePage from './pages/AttendancePage';
import ReportsPage from './pages/ReportsPage';
import ProductivityPage from './pages/ProductivityPage';
import SettingsPage from './pages/SettingsPage';
import MessageTemplatesPage from './pages/MessageTemplatesPage';
import CertificatePage from './pages/CertificatePage';
import RolesPage from './pages/RolesPage';
import CompanyConfigPage from './pages/CompanyConfigPage';
import CommissionsPage from './pages/CommissionsPage';
import MyCommissionsPage from './pages/MyCommissionsPage';
import CashCutsAuditPage from './pages/CashCutsAuditPage';
import TicketPage from './pages/TicketPage';
import PrinterSetupPage from './pages/PrinterSetupPage';
import CustomerLoginPage from './pages/CustomerLoginPage';
import CustomerAuthPage from './pages/CustomerAuthPage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import CustomerDirectoryPage from './pages/CustomerDirectoryPage';
import ReceiptConfigPage from './pages/ReceiptConfigPage';
import PaymentTerminalsPage from './pages/PaymentTerminalsPage';
import MySalesPage from './pages/MySalesPage';

// Components
import Sidebar from './components/Sidebar';
import LocationSelectorModal from './components/LocationSelectorModal';

// Role-based home redirect
const HomeRoute = () => {
    const { user, can, canAny } = useAuth();
    // Customer-service users land in their directory
    if (user?.role === 'customer_service' || (canAny('customers.read_all', 'customers.read_limited') && !canAny('reports.dashboard_global', 'reports.dashboard_own'))) {
        return <Navigate to="/customer-directory" replace />;
    }
    return <DashboardPage />;
};

// Protected Route Wrapper - supports both legacy allowedRoles and granular permission checks
const ProtectedRoute = ({ children, allowedRoles, requires, requiresAny }) => {
    const { user, loading, can, canAny } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FB7185] border-t-transparent"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Permission gate: prefer granular over role
    if (requires && !can(requires)) {
        return <Navigate to="/" replace />;
    }
    if (requiresAny && !canAny(...requiresAny)) {
        return <Navigate to="/" replace />;
    }
    // Legacy fallback
    if (allowedRoles && !requires && !requiresAny && !allowedRoles.includes(user.role)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

// Layout with Sidebar
const AppLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [locationModalIsInitialPrompt, setLocationModalIsInitialPrompt] = useState(false);
    const { user, can } = useAuth();

    // Sellers (or anyone with POS access) MUST pick a location at the start of
    // each session. We track per-user-id in sessionStorage so the prompt fires
    // on every fresh login but doesn't keep nagging within the same tab.
    // IMPORTANT: include `canPos` in deps so the effect re-runs once permissions
    // hydrate after login (initial setUser commit may precede the perms commit).
    const canPos = can('pos.access');
    React.useEffect(() => {
        if (!user) return;
        if (user.role === 'admin') return;
        if (!canPos) return;
        const sessionKey = `location_selected_${user.id}`;
        const stored = sessionStorage.getItem(sessionKey);
        if (!stored || stored !== user.current_location_id) {
            setLocationModalIsInitialPrompt(true);
            setLocationModalOpen(true);
        } else {
            setLocationModalIsInitialPrompt(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, canPos]);

    const openLocationSwitcher = () => {
        setLocationModalIsInitialPrompt(false);
        setLocationModalOpen(true);
    };

    return (
        <div className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                onOpenLocationSwitcher={openLocationSwitcher}
            />

            <main className="flex-1 min-w-0 lg:ml-0">
                {/* Mobile Header */}
                <header
                    className="lg:hidden sticky top-0 z-30 h-16 flex items-center px-4 bg-[#1F2D4B] text-white"
                >
                    <button
                        onClick={() => setSidebarOpen(true)}
                        data-testid="mobile-menu-btn"
                        className="p-2 rounded-md hover:bg-white/10 transition-colors"
                    >
                        <List size={20} />
                    </button>
                    <svg viewBox="0 0 800 375" className="ml-3 h-9" aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
                        <path d="M 165 245 Q 400 110 635 245" fill="none" stroke="#FB7185" strokeWidth="10" strokeLinecap="round" />
                        <text x="400" y="340" textAnchor="middle" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="92" fontWeight="700" fill="#FFFFFF" letterSpacing="-2.5">exploremos</text>
                    </svg>
                </header>

                {/* Page Content */}
                <div className="p-6 lg:p-8 xl:p-10">
                    <ErrorBoundary>
                        <Outlet />
                    </ErrorBoundary>
                </div>
            </main>

            <LocationSelectorModal
                open={locationModalOpen}
                onClose={() => setLocationModalOpen(false)}
                isInitialPrompt={locationModalIsInitialPrompt}
            />
        </div>
    );
};

// Auth Layout
const AuthLayout = () => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FB7185] border-t-transparent"></div>
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return <Outlet />;
};

function App() {
    return (
        <AuthProvider>
            <Toaster 
                position="top-center" 
                richColors 
                toastOptions={{
                    style: {
                        borderRadius: '12px',
                    },
                }}
            />
            <BrowserRouter>
                <Routes>
                    {/* Public Routes (no auth) */}
                    <Route path="/cert/:folio" element={<CertificatePage />} />

                    {/* Customer Portal (Magic Link auth — separate from staff JWT) */}
                    <Route path="/me/login" element={<CustomerLoginPage />} />
                    <Route path="/me/auth" element={<CustomerAuthPage />} />
                    <Route path="/me" element={<CustomerPortalPage />} />
                    
                    {/* Auth Routes */}
                    <Route element={<AuthLayout />}>
                        <Route path="/login" element={<LoginPage />} />
                    </Route>
                    
                    {/* Protected Routes */}
                    <Route element={
                        <ProtectedRoute>
                            <AppLayout />
                        </ProtectedRoute>
                    }>
                        <Route path="/" element={<HomeRoute />} />
                        <Route path="/pos" element={
                            <ProtectedRoute requires="pos.access"><POSPage /></ProtectedRoute>
                        } />
                        <Route path="/sales" element={
                            <ProtectedRoute requires="sales.read_all"><SalesPage /></ProtectedRoute>
                        } />
                        <Route path="/payments-validation" element={
                            <ProtectedRoute requires="payments.validate_view"><PaymentsValidationPage /></ProtectedRoute>
                        } />
                        <Route path="/attendance" element={
                            <ProtectedRoute requires="attendance.read_all"><AttendancePage /></ProtectedRoute>
                        } />
                        
                        {/* Admin & Manager Routes */}
                        <Route path="/users" element={
                            <ProtectedRoute requires="users.read_all">
                                <UsersPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/products" element={
                            <ProtectedRoute requires="products.read">
                                <ProductsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/reports" element={
                            <ProtectedRoute requires="reports.sales_report">
                                <ReportsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/productivity" element={
                            <ProtectedRoute requiresAny={['reports.dashboard_global', 'reports.export']}>
                                <ProductivityPage />
                            </ProtectedRoute>
                        } />
                        
                        {/* Admin Only Routes */}
                        <Route path="/locations" element={
                            <ProtectedRoute requires="locations.read">
                                <LocationsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/settings" element={
                            <ProtectedRoute requires="messaging.settings_view">
                                <SettingsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/templates" element={
                            <ProtectedRoute requires="templates.read">
                                <MessageTemplatesPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/roles" element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <RolesPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/company" element={
                            <ProtectedRoute requires="company.update">
                                <CompanyConfigPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/printer-setup" element={
                            <ProtectedRoute requires="company.receipt_config">
                                <PrinterSetupPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/commissions" element={
                            <ProtectedRoute requires="commissions.read_all">
                                <CommissionsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/my-commissions" element={
                            <ProtectedRoute requires="commissions.read_own">
                                <MyCommissionsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/cash-cuts-audit" element={
                            <ProtectedRoute requires="cash_cuts.audit_detail">
                                <CashCutsAuditPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/customer-directory" element={
                            <ProtectedRoute requiresAny={['customers.read_all', 'customers.read_limited']}>
                                <CustomerDirectoryPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/receipt-config" element={
                            <ProtectedRoute requires="company.receipt_config">
                                <ReceiptConfigPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/payment-terminals" element={
                            <ProtectedRoute requires="company.payment_terminals">
                                <PaymentTerminalsPage />
                            </ProtectedRoute>
                        } />
                        <Route path="/my-sales" element={
                            <ProtectedRoute requires="sales.read_own">
                                <MySalesPage />
                            </ProtectedRoute>
                        } />
                    </Route>

                    {/* Protected — full-screen (no sidebar) for clean printing */}
                    <Route path="/tickets/:saleId" element={
                        <ProtectedRoute>
                            <TicketPage />
                        </ProtectedRoute>
                    } />
                    
                    {/* Catch all */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
